import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDevices } from '../../context/DeviceContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { deriveStatus, DEVICE_STATUS, STATUS_LABEL, formatLastSeen, signalQuality } from '../../domain/device';
/* Sin checkLimit: los planes se muestran, no se aplican. */
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import SignalCellularAltRoundedIcon from '@mui/icons-material/SignalCellularAltRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import './Devices.css';

const SIGNAL_LABEL = { strong: 'Señal fuerte', medium: 'Señal media', weak: 'Señal débil' };

/** Confirmación de desvinculación. Pide escribir el nombre porque es destructivo. */
function UnlinkDialog({ device, onCancel, onConfirm, working }) {
  const [texto, setTexto] = useState('');
  const nombre = device.device_name || device.device_id;
  const coincide = texto.trim() === nombre;

  return (
    <div className="dv-overlay" role="dialog" aria-modal="true" onMouseDown={onCancel}>
      <div className="dv-dialog" onMouseDown={(e) => e.stopPropagation()}>
        <span className="dv-dialog__icon"><WarningAmberRoundedIcon /></span>
        <h3>Desvincular «{nombre}»</h3>
        <p>
          El dispositivo dejará de enviar datos a tu cuenta y perderás el acceso a su historial.
          Podrás volver a vincularlo más adelante con su código de activación.
        </p>
        <label className="dv-dialog__field">
          <span>Escribe <strong>{nombre}</strong> para confirmar</span>
          <input
            className="dv-dialog__input"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            autoFocus
            autoComplete="off"
          />
        </label>
        <div className="dv-dialog__actions">
          <button className="dv-btn" onClick={onCancel} disabled={working}>Cancelar</button>
          <button
            className="dv-btn dv-btn--danger"
            onClick={() => onConfirm(device.device_id)}
            disabled={!coincide || working}
          >
            {working ? 'Desvinculando…' : 'Desvincular'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Devices() {
  const navigate = useNavigate();
  const { devices, loading, error, selectDevice, unlinkDevice, renameDevice, selectedDeviceId } = useDevices();
  const { plan } = useSubscription();

  const [porDesvincular, setPorDesvincular] = useState(null);
  const [trabajando, setTrabajando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [borrador, setBorrador] = useState({ name: '', location: '' });

  const confirmarDesvinculacion = async (deviceId) => {
    setTrabajando(true);
    try {
      await unlinkDevice(deviceId);
      setPorDesvincular(null);
    } finally {
      setTrabajando(false);
    }
  };

  const guardarNombre = async (deviceId) => {
    setTrabajando(true);
    try {
      await renameDevice(deviceId, borrador);
      setEditando(null);
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <div className="dv-page">
      <header className="dv-head">
        <div className="dv-head__info">
          <h2 className="section-title">Dispositivos vinculados</h2>
          <p className="dv-head__meta">
            {devices.length === 1 ? '1 dispositivo' : `${devices.length} dispositivos`} · Plan {plan.name}
          </p>
        </div>
        <button
          className="dv-btn dv-btn--primary"
          onClick={() => navigate('/devices/link')}
        >
          <AddRoundedIcon fontSize="small" /> Vincular
        </button>
      </header>

      {error && <div className="dv-notice dv-notice--error">{error}</div>}

      {loading && devices.length === 0 && (
        <div className="dv-list">
          {[0, 1].map((i) => <div key={i} className="dv-card dv-skeleton" />)}
        </div>
      )}

      {!loading && devices.length === 0 && (
        <div className="dv-empty">
          <span className="dv-empty__icon"><DevicesRoundedIcon /></span>
          <h3>No hay dispositivos en tu cuenta</h3>
          <p>Vincula tu NisaMeter con el número de serie y el código de activación de la etiqueta.</p>
          <button className="dv-btn dv-btn--primary" onClick={() => navigate('/devices/link')}>
            <AddRoundedIcon fontSize="small" /> Vincular dispositivo
          </button>
        </div>
      )}

      <div className="dv-list">
        {devices.map((d, i) => {
          const status = deriveStatus(d);
          const señal = signalQuality(d);
          const activo = d.device_id === selectedDeviceId;
          const enEdicion = editando === d.device_id;

          return (
            <article key={d.device_id} className={`dv-card ${activo ? 'is-active' : ''}`} style={{ '--i': i }}>
              <div className="dv-card__main">
                <span className={`dv-status dv-status--${status}`} title={STATUS_LABEL[status]}>
                  <span className="dv-status__dot" />
                </span>

                <div className="dv-card__info">
                  {enEdicion ? (
                    <div className="dv-edit">
                      <input
                        className="dv-edit__input"
                        value={borrador.name}
                        onChange={(e) => setBorrador((b) => ({ ...b, name: e.target.value }))}
                        placeholder="Nombre del dispositivo"
                        autoFocus
                      />
                      <input
                        className="dv-edit__input dv-edit__input--sm"
                        value={borrador.location}
                        onChange={(e) => setBorrador((b) => ({ ...b, location: e.target.value }))}
                        placeholder="Ubicación"
                      />
                      <div className="dv-edit__actions">
                        <button className="dv-btn dv-btn--sm" onClick={() => setEditando(null)} disabled={trabajando}>
                          Cancelar
                        </button>
                        <button
                          className="dv-btn dv-btn--sm dv-btn--primary"
                          onClick={() => guardarNombre(d.device_id)}
                          disabled={trabajando}
                        >
                          Guardar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h3 className="dv-card__name">
                        {d.device_name || d.device_id}
                        {activo && <span className="dv-badge">Viendo ahora</span>}
                      </h3>
                      <p className="dv-card__meta">
                        {STATUS_LABEL[status]}
                        {status !== DEVICE_STATUS.ONLINE && ` · ${formatLastSeen(d)}`}
                        {d.location && ` · ${d.location}`}
                      </p>
                      <p className="dv-card__ids">
                        <code>{d.serial || d.device_id}</code>
                        {d.firmware_version && <span>Firmware {d.firmware_version}</span>}
                        {señal && (
                          <span className={`dv-signal dv-signal--${señal}`}>
                            <SignalCellularAltRoundedIcon fontSize="inherit" />
                            {SIGNAL_LABEL[señal]}
                          </span>
                        )}
                      </p>
                    </>
                  )}
                </div>
              </div>

              {!enEdicion && (
                <div className="dv-card__actions">
                  <button
                    className="dv-icon-btn"
                    title="Ver este dispositivo"
                    onClick={() => { selectDevice(d.device_id); navigate('/home'); }}
                  >
                    <VisibilityRoundedIcon fontSize="small" />
                  </button>
                  <button
                    className="dv-icon-btn"
                    title="Editar nombre y ubicación"
                    onClick={() => {
                      setEditando(d.device_id);
                      setBorrador({ name: d.device_name || '', location: d.location || '' });
                    }}
                  >
                    <EditRoundedIcon fontSize="small" />
                  </button>
                  <button
                    className="dv-icon-btn dv-icon-btn--danger"
                    title="Desvincular"
                    onClick={() => setPorDesvincular(d)}
                  >
                    <LinkOffRoundedIcon fontSize="small" />
                  </button>
                </div>
              )}
            </article>
          );
        })}
      </div>

      {porDesvincular && (
        <UnlinkDialog
          device={porDesvincular}
          working={trabajando}
          onCancel={() => !trabajando && setPorDesvincular(null)}
          onConfirm={confirmarDesvinculacion}
        />
      )}
    </div>
  );
}
